import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  PermissionKey,
  PermissionOverrideValue,
  UserPermissionsPatchResponse,
  UserRole,
  UsersResponse,
} from './contractsMirror.js';
import { mergePermissionPatchResponse } from './contractsMirror.js';
import { usersQueryKey } from './useUsersQuery.js';

/**
 * The permission-override write hook for the interactive permissions matrix (SEC-sensitive,
 * T-22-privesc). A toggle change PATCHes `/api/v1/users/:id/permissions` with only the
 * touched tri-state override. The SERVER re-checks the acting user's
 * role, enforces the ≥1-active-Admin invariant (409 → rejected, not persisted), and emits an
 * audit event (SEC-09); the UI mirrors that invariant but the server is authoritative.
 *
 * The request carries NO tenantId — the tenant is the auth principal server-side
 * (threat T-22-tenant). It rides `apiFetch`'s double-submit CSRF on the non-safe method.
 * On success we invalidate the users directory so the list re-syncs to the server state.
 */

/**
 * Exactly ONE of these is sent per call, never both:
 *   - `{ role }`      — the role-card selector, the primary access surface.
 *   - `{ overrides }` — a single tri-state toggle on the Advanced screen.
 *
 * The endpoint accepts both together, but the UI never mixes them: a role change and an
 * override are two different decisions, each with its own confirm and its own audit
 * entry, and a combined body would make the activity log ambiguous about which one the
 * admin actually intended. The body is built from whichever key is present so the wire
 * shape stays minimal and the contract-parity tests can assert on it exactly.
 */
export interface PermissionsMutationInput {
  userId: number;
  role?: UserRole;
  overrides?: Partial<Record<PermissionKey, PermissionOverrideValue>>;
}

export function usePermissionsMutation() {
  const queryClient = useQueryClient();

  return useMutation<UserPermissionsPatchResponse, Error, PermissionsMutationInput>({
    mutationFn: ({ userId, role, overrides }) =>
      apiFetchJson<UserPermissionsPatchResponse>(`/api/v1/users/${userId}/permissions`, {
        method: 'PATCH',
        // NO tenantId — the server resolves the tenant from the principal (SEC-11).
        // NOTE: `JSON.stringify` already drops an `undefined` value, so a naive
        // `{ role, overrides }` happens to produce the right bytes today. This stays
        // explicit anyway — the moment either side is defaulted to `{}` the wire shape
        // changes silently, which is exactly what the wire-shape test caught.
        body: JSON.stringify(role !== undefined ? { role } : { overrides }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (response, input) => {
      queryClient.setQueryData<UsersResponse>(usersQueryKey(), (current) =>
        mergePermissionPatchResponse(current, input.userId, response),
      );
      void queryClient.invalidateQueries({ queryKey: usersQueryKey() });
    },
  });
}
